import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { XingId } from '@xingternal/hub';
import { ProfileImage } from '@xingternal/profile-image';
import { ProfileInfo } from '@xingternal/profile-info';
import { BodyCopy, Headline } from '@xingternal/typography';

import { Gender, useApplyFormUserLazyQuery } from '@/graphql/generated';
import { useOnFocus } from '@/hooks';

import { getProfileUrlWithTracking } from '../helpers';

import * as S from './profile-card.styles';

type Props = {
  user?: XingId;
};

const getProfileImageGender = (gender?: string): 'female-user' | 'male-user' | 'neutral-user' => {
  if (gender === Gender.Female) return 'female-user';
  if (gender === Gender.Male) return 'male-user';
  return 'neutral-user';
};

export const ProfileCard: React.FC<Props> = ({ user }) => {
  const [getApplyFormUser, { data: userData }] = useApplyFormUserLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const hasBeenFocused = useOnFocus();
  const role = userData?.viewer?.xingId?.occupations?.[0]?.subline ?? user?.occupations?.[0]?.subline;
  const company = userData?.viewer?.xingId?.occupations?.[0]?.headline ?? user?.occupations?.[0]?.headline;

  useEffect(() => {
    if (hasBeenFocused) {
      getApplyFormUser();
    }
  }, [getApplyFormUser, hasBeenFocused]);

  return (
    <S.Link
      target="_blank"
      data-cy="instant-apply-profile-preview-link"
      href={
        user
          ? getProfileUrlWithTracking({
              user,
              trackingParams: 'sc_o=jobs_profile_self_image_edit',
            })
          : undefined
      }
    >
      <ProfileInfo
        profileImage={
          <ProfileImage
            size="medium"
            src={userData?.viewer?.xingId?.profileImage?.[0]?.url ?? user?.profileImage?.[0]?.url}
            type={getProfileImageGender(user?.gender)}
            profileName={user?.displayName}
          />
        }
      >
        <Headline size="medium">{user?.displayName}</Headline>
        {role || company ? (
          <BodyCopy size="medium">
            <S.ProfileInfoTextWrapper>
              {role && company ? (
                <FormattedMessage
                  id="JOBS_POSTING_EASY_APPLY_FORM_PROFILE_INFO_OCCUPATION"
                  values={{
                    role,
                    company,
                  }}
                />
              ) : role ? (
                role
              ) : (
                company
              )}
            </S.ProfileInfoTextWrapper>
          </BodyCopy>
        ) : null}
      </ProfileInfo>
    </S.Link>
  );
};
